import { c as _c } from "react/compiler-runtime";
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledInput } from 'shared/input';
import { useAutocompleteSuggestions } from './use-autocomplete-suggestions';
import { useFloating, autoUpdate, offset, flip, size, useInteractions, useRole, useDismiss, useListNavigation, useClick } from '@floating-ui/react';
import { Box } from 'shared/grid';
interface Props {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
}
export const MapAutocomplete = t0 => {
  const $ = _c(61);
  const {
    onPlaceSelect
  } = t0;
  const map = useMap();
  const places = useMapsLibrary("places");
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(null);
  let t1;
  if ($[0] !== map) {
    t1 = map.getCenter();
    $[0] = map;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      locationBias: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    suggestions,
    resetSession,
    isLoading
  } = useAutocompleteSuggestions(inputValue, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const listRef = useRef(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [offset(5), flip(), size({
      apply(t5) {
        const {
          rects,
          elements
        } = t5;
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxHeight: "300px"
        });
      }
    })];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== open) {
    t5 = {
      open,
      onOpenChange: setOpen,
      placement: "bottom-start",
      middleware: t4,
      whileElementsMounted: autoUpdate
    };
    $[6] = open;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    refs,
    floatingStyles,
    context
  } = useFloating(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      role: "listbox"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = useRole(context, t6);
  const t8 = useDismiss(context);
  let t9;
  if ($[9] !== activeIndex) {
    t9 = {
      listRef,
      activeIndex,
      onNavigate: setActiveIndex,
      virtual: true,
      loop: true
    };
    $[9] = activeIndex;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = useListNavigation(context, t9);
  const t11 = useClick(context);
  let t12;
  if ($[11] !== t10 || $[12] !== t11 || $[13] !== t7 || $[14] !== t8) {
    t12 = [t7, t8, t10, t11];
    $[11] = t10;
    $[12] = t11;
    $[13] = t7;
    $[14] = t8;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions(t12);
  let t13;
  if ($[16] !== inputValue || $[17] !== suggestions.length) {
    t13 = () => {
      if (suggestions.length > 0 && inputValue) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };
    $[16] = inputValue;
    $[17] = suggestions.length;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== inputValue || $[20] !== suggestions) {
    t14 = [suggestions, inputValue];
    $[19] = inputValue;
    $[20] = suggestions;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  useEffect(t13, t14);
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event => {
      setInputValue((event.target as HTMLInputElement).value);
      setActiveIndex(null);
    };
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const handleInput = t15;
  let t16;
  if ($[23] !== map || $[24] !== onPlaceSelect || $[25] !== places || $[26] !== resetSession) {
    t16 = async suggestion => {
      if (!places) {
        return;
      }
      if (!suggestion.placePrediction) {
        return;
      }
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["svgIconMaskURI", "iconBackgroundColor", "displayName", "formattedAddress", "location", "viewport", "addressComponents"]
      });
      setInputValue("");
      setOpen(false);
      resetSession();
      if (place?.viewport) {
        map.fitBounds(place.viewport);
      }
      onPlaceSelect(place);
    };
    $[23] = map;
    $[24] = onPlaceSelect;
    $[25] = places;
    $[26] = resetSession;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const handleSuggestionClick = t16;
  let t17;
  if ($[28] !== activeIndex || $[29] !== handleSuggestionClick || $[30] !== open || $[31] !== suggestions) {
    t17 = event_0 => {
      if (event_0.key === "Enter" || event_0.key === "Tab") {
        if (activeIndex !== null && suggestions[activeIndex]) {
          event_0.preventDefault();
          handleSuggestionClick(suggestions[activeIndex]);
          return;
        }
      } else {
        if (event_0.key === "ArrowDown") {
          event_0.preventDefault();
          if (!open) {
            setOpen(true);
            setActiveIndex(0);
            return;
          }
          if (activeIndex === null) {
            setActiveIndex(0);
          } else {
            const nextIndex = (activeIndex + 1) % suggestions.length;
            setActiveIndex(nextIndex);
          }
        } else {
          if (event_0.key === "ArrowUp") {
            event_0.preventDefault();
            if (activeIndex === null) {
              setActiveIndex(suggestions.length - 1);
            } else {
              const prevIndex = (activeIndex - 1 + suggestions.length) % suggestions.length;
              setActiveIndex(prevIndex);
            }
          } else {
            if (event_0.key === "Escape") {
              setOpen(false);
              setActiveIndex(null);
              event_0.preventDefault();
            }
          }
        }
      }
    };
    $[28] = activeIndex;
    $[29] = handleSuggestionClick;
    $[30] = open;
    $[31] = suggestions;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const handleKeyDown = t17;
  const t18 = refs.setReference;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      backgroundColor: "background",
      width: "100%",
      textOverflow: "ellipsis"
    };
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event_1 => handleInput(event_1);
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== handleKeyDown) {
    t21 = event_2 => {
      const isHandledByUs = ["Enter", "Tab", "ArrowUp", "ArrowDown", "Escape"].includes(event_2.key);
      if (isHandledByUs) {
        handleKeyDown(event_2);
      }
    };
    $[35] = handleKeyDown;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== getReferenceProps || $[38] !== t21) {
    t22 = getReferenceProps({
      onKeyDown: t21
    });
    $[37] = getReferenceProps;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== handleKeyDown || $[41] !== inputValue || $[42] !== refs.setReference || $[43] !== t22) {
    t23 = <StyledInput ref={t18} placeholder="Search..." sx={t19} value={inputValue} onInput={t20} onKeyDown={handleKeyDown} {...t22} />;
    $[40] = handleKeyDown;
    $[41] = inputValue;
    $[42] = refs.setReference;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== activeIndex || $[46] !== floatingStyles || $[47] !== getFloatingProps || $[48] !== getItemProps || $[49] !== handleKeyDown || $[50] !== handleSuggestionClick || $[51] !== open || $[52] !== refs.setFloating || $[53] !== suggestions) {
    t24 = open && suggestions.length > 0 && <Box ref={refs.setFloating} style={{
      ...floatingStyles,
      overflowY: "auto",
      padding: "8px 0",
      margin: 0,
      backgroundColor: "white",
      borderRadius: "4px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)"
    }} {...getFloatingProps({
      onKeyDown: handleKeyDown
    })} className="custom-list">{suggestions.map((suggestion_0, index) => <Box key={index} id={`suggestion-${index}`} ref={node => {
        listRef.current[index] = node;
      }} style={{
        padding: "8px 12px",
        cursor: "pointer",
        backgroundColor: activeIndex === index ? "#f0f0f0" : "transparent"
      }} className="custom-list-item" role="option" aria-selected={activeIndex === index} tabIndex={-1} {...getItemProps({
        onClick: () => handleSuggestionClick(suggestion_0),
        onKeyDown: e => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSuggestionClick(suggestion_0);
          }
        }
      })}>{suggestion_0.placePrediction?.text.text}</Box>)}</Box>;
    $[45] = activeIndex;
    $[46] = floatingStyles;
    $[47] = getFloatingProps;
    $[48] = getItemProps;
    $[49] = handleKeyDown;
    $[50] = handleSuggestionClick;
    $[51] = open;
    $[52] = refs.setFloating;
    $[53] = suggestions;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== isLoading) {
    t25 = isLoading && <Box style={{
      padding: "8px"
    }}>Loading...</Box>;
    $[55] = isLoading;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t23 || $[58] !== t24 || $[59] !== t25) {
    t26 = <Box className="autocomplete-container">{t23}{t24}{t25}</Box>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};